import mitt from 'mitt'

/**
 * Vue 2 compatible event bus built on top of mitt.
 *
 * Vue 2's `$emit` forwarded every argument to listeners
 * (`$emit('evt', a, b, c)` -> `handler(a, b, c)`), but mitt only supports a
 * single payload per event and invokes listeners as `handler(payload)`.
 *
 * To preserve the original multi-argument behaviour, every emit packs its
 * arguments into an array payload and every listener is wrapped so the array
 * is spread back into the original handler. Wrappers are tracked per
 * (event, handler) so `$off` can remove the correct mitt listener.
 */
function createVue2EventBus () {
  const emitter = mitt()
  // event -> Map<originalHandler, wrapper[]>
  const registry = new Map()

  function track (event, handler, wrapper) {
    let handlers = registry.get(event)
    if (!handlers) {
      handlers = new Map()
      registry.set(event, handlers)
    }
    const wrappers = handlers.get(handler)
    if (wrappers) {
      wrappers.push(wrapper)
    } else {
      handlers.set(handler, [wrapper])
    }
  }

  function untrack (event, handler, wrapper) {
    const handlers = registry.get(event)
    if (!handlers) {
      return
    }
    const wrappers = handlers.get(handler)
    if (!wrappers) {
      return
    }
    const index = wrappers.indexOf(wrapper)
    if (index !== -1) {
      wrappers.splice(index, 1)
    }
    if (wrappers.length === 0) {
      handlers.delete(handler)
    }
    if (handlers.size === 0) {
      registry.delete(event)
    }
  }

  return {
    $on (event, handler) {
      const wrapper = (args) => {
        handler.apply(null, Array.isArray(args) ? args : [args])
      }
      track(event, handler, wrapper)
      emitter.on(event, wrapper)
    },
    $once (event, handler) {
      const wrapper = (args) => {
        emitter.off(event, wrapper)
        untrack(event, handler, wrapper)
        handler.apply(null, Array.isArray(args) ? args : [args])
      }
      track(event, handler, wrapper)
      emitter.on(event, wrapper)
    },
    $off (event, handler) {
      if (event === undefined) {
        emitter.all.clear()
        registry.clear()
        return
      }
      if (handler === undefined) {
        emitter.off(event)
        registry.delete(event)
        return
      }
      const handlers = registry.get(event)
      if (handlers && handlers.has(handler)) {
        for (const wrapper of handlers.get(handler).slice()) {
          emitter.off(event, wrapper)
        }
        handlers.delete(handler)
        if (handlers.size === 0) {
          registry.delete(event)
        }
      } else {
        // Fallback for listeners that may have been registered directly.
        emitter.off(event, handler)
      }
    },
    $emit (event, ...args) {
      emitter.emit(event, args)
    }
  }
}

export const eventBus = createVue2EventBus()
export default createVue2EventBus
